import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  getInterventionList,
  setDressingAppearance,
  setDebridement,
  setWoundCleanserGetData,
  setSkinProtectantGetData,
  setPrimaryDressingGetData,
  setSecondaryDressingGetData,
  setCompressionGetData,
  setModalities,
  setAdditionalCare,
  setInterventionListOptions,
  setSelectedInterventionId,
  setSelectedInterventionName,
} from "../../../../../app/patientSlice";
import { LOADING_KEYS } from "../../../../../constants/loadingKeys";
import { useLoading } from "../../../../../hooks/useLoading";
import { usePermissions } from "../../../../../hooks/usePermissions";

export const useTreatment = () => {
  const dispatch = useDispatch();
  const { patientId, assessmentId } = useParams();

  const { hasPermission } = usePermissions();
  const patientData = useSelector((state) => state.patient.detailViewData);
  const assessmentDetails = useSelector((state) => state.patient.detailAssessment);
  const listOptions = useSelector((state) => state.patient.interventionListOptions);
  const selectedInterventionId = useSelector((state) => state.patient.selectedInterventionId);
  const selectedInterventionName = useSelector((state) => state.patient.selectedInterventionName);
  const treatmentList = useSelector((s) => s.patient.minimumDatasetInfo?.treatment);
  const dynTab = useMemo(
    () => treatmentList?.find((item) => item?.name === selectedInterventionName)?.items || [],
    [treatmentList, selectedInterventionName]
  );
  const interventionLoader = useLoading(LOADING_KEYS.INTERVENTION_TREATMENT);

  const [viewDropDown, setDropDown] = useState(false);
  const [defaultIndex, setDefaultIndex] = useState("");
  const [formularyId, setFormularyId] = useState(patientData?.formularyId);
  const dropDownRef = useRef();

  const resetTreatmentState = useCallback(() => {
    dispatch(setDressingAppearance({}));
    dispatch(setDebridement({}));
    dispatch(setWoundCleanserGetData({}));
    dispatch(setSkinProtectantGetData({}));
    dispatch(setPrimaryDressingGetData({}));
    dispatch(setSecondaryDressingGetData({}));
    dispatch(setCompressionGetData({}));
    dispatch(setModalities({}));
    dispatch(setAdditionalCare({}));
  }, [dispatch]);

  useEffect(() => {
    dispatch(setInterventionListOptions([]));
    resetTreatmentState();
  }, [dispatch, assessmentId]);

  useEffect(() => {
    if (assessmentId) {
      dispatch(getInterventionList({ patientId, assessmentId }));
    }
  }, [dispatch, assessmentId, patientId]);

  useEffect(() => {
    if (patientData?.formularyId) {
      setFormularyId(patientData.formularyId);
    }
  }, [patientData?.formularyId]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropDownRef.current && !dropDownRef.current.contains(e.target)) {
        setDropDown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDropDown = useCallback(() => setDropDown((prev) => !prev), []);

  const handleDropChange = useCallback(
    (e, i, optionValues) => {
      if (optionValues) {
        if (optionValues.interventionId === selectedInterventionId) {
          setDropDown(false);
          return;
        }
        resetTreatmentState();
        dispatch(setSelectedInterventionName(optionValues.interventionName));
        dispatch(setSelectedInterventionId(optionValues.interventionId));
        setDefaultIndex(0);
      }
      setDropDown(false);
    },
    [dispatch, resetTreatmentState, selectedInterventionId]
  );

  const handleClickAddIntervention = useCallback(() => {
    if (!assessmentId) return;
    resetTreatmentState();
    dispatch(setSelectedInterventionId(0));
    dispatch(setSelectedInterventionName("Intervention"));
  }, [dispatch, assessmentId, resetTreatmentState]);

  const isInterventionComplete = useMemo(() => Array.isArray(dynTab) && dynTab.some((d) => d.isCompleted === 1), [dynTab]);

  return {
    patientData,
    assessmentDetails,
    interventionLoader,
    listOptions,
    selectedInterventionId,
    selectedInterventionName,
    dynTab,
    viewDropDown,
    defaultIndex,
    formularyId,
    dropDownRef,
    isInterventionComplete,
    hasPermission,
    handleDropDown,
    handleDropChange,
    handleClickAddIntervention,
  };
};
